import { getDisplayToken } from '@/domain/hooks/useDisplayToken'
import { Token } from '@/domain/types/Token'
import { SuccessViewCheckmark } from '@/features/dialogs/common/components/success-view/SuccessViewCheckmark'
import { SuccessViewContent } from '@/features/dialogs/common/components/success-view/SuccessViewContent'
import { SuccessViewProceedButton } from '@/features/dialogs/common/components/success-view/SuccessViewProceedButton'
import { SuccessViewSummaryPanel } from '@/features/dialogs/common/components/success-view/SuccessViewSummaryPanel'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { useChainId } from 'wagmi'

export interface CollateralStatusSuccessViewProps {
  token: Token
  action: 'enable' | 'disable'
  onProceed: () => void
  proceedText?: string
  /** Dialog mode: 'collateral' for standard flow, 'controller' for different-market vaults */
  mode?: 'collateral' | 'controller'
}

export function CollateralStatusSuccessView({
  token,
  action,
  onProceed,
  proceedText = 'Close',
  mode = 'collateral',
}: CollateralStatusSuccessViewProps) {
  const chainId = useChainId()
  const displayToken = getDisplayToken(chainId, token)

  const isControllerMode = mode === 'controller'
  const title = isControllerMode
    ? action === 'enable'
      ? 'Controller Enabled'
      : 'Controller Disabled'
    : action === 'enable'
      ? 'Collateral Enabled'
      : 'Transferred to Savings'
  const description = isControllerMode
    ? action === 'enable'
      ? 'Controller has been enabled for this vault'
      : 'Controller has been disabled for this vault'
    : action === 'enable'
      ? 'Now backing your borrowing position'
      : 'Now earning yield without backing debt'

  return (
    <SuccessViewContent>
      <SuccessViewCheckmark isModal />
      <SuccessViewSummaryPanel title={title}>
        <div className="flex items-center gap-2">
          <TokenIcon token={token} className="h-6" />
          <span className="font-semibold">{displayToken.symbol}</span>
        </div>
        <div className="flex grow flex-col">
          <Typography className="text-right font-primary text-muted-foreground text-sm">{description}</Typography>
        </div>
      </SuccessViewSummaryPanel>
      <SuccessViewProceedButton onProceed={onProceed}>{proceedText}</SuccessViewProceedButton>
    </SuccessViewContent>
  )
}
